import { useCallback, useContext, useMemo } from "react";
import { Section } from "@models/ISchema";
import { ContextEDH } from "@context/ContextEDH";
import { AppContext } from "../context/EditSessionsContext";
import { usePastSessionsVisibilityStore } from "../context/usePastSessionsVisibilityStore";
import { getSectionPastSessions } from "../utils/getSectionPastSessions";

interface UseSectionPastSessions {
  pastSessionIds: Set<string>;
  hasPastAndActiveSessions: boolean;
  showPastSessions: boolean;
  toggleShowPastSessions: () => void;
}

export const useSectionPastSessions = (section: Section): UseSectionPastSessions => {
  const { state } = useContext(AppContext);
  const { state: stateEDH } = useContext(ContextEDH);
  const showPastSessionsBySectionId = usePastSessionsVisibilityStore(
    s => s.showPastSessionsBySectionId,
  );
  const toggleShowPastSessionsBySection = usePastSessionsVisibilityStore(
    s => s.toggleShowPastSessionsBySection,
  );

  const thresholdDate = state?.form?.blockingThreshold;
  const canEditPastSessions = !!stateEDH?.base?.base?.user?.abilities?.can_edit_past_sessions;

  const { pastSessionIds, hasPastAndActiveSessions } = useMemo(
    () => getSectionPastSessions(section, thresholdDate, canEditPastSessions),
    [section, thresholdDate, canEditPastSessions],
  );

  const pastSessionIdsSet = useMemo(() => new Set(pastSessionIds), [pastSessionIds]);

  const toggleShowPastSessions = useCallback(
    () => toggleShowPastSessionsBySection(section?.id),
    [toggleShowPastSessionsBySection, section?.id],
  );

  return {
    pastSessionIds: pastSessionIdsSet,
    hasPastAndActiveSessions,
    showPastSessions: !!showPastSessionsBySectionId[section?.id],
    toggleShowPastSessions,
  };
};
